/**
 * CollectionsPage — 컬렉션 탭.
 *
 * ⚠ 기획확정안(§6) 기준: **포트폴리오와 컬렉션을 분리하지 않는다.**
 * 구매가·구매일·구매처는 컬렉션 저장 항목(§6.2)이고, 총 평가액·가치 변화도
 * 컬렉션이 보여준다(§6.3). MonPrice 의 포트폴리오/컬렉션 2탭 구조를 따르지 않는다.
 *
 * §6.3 표시 항목 중 이 화면이 담당하는 것: 전체 보유 카드 수 · 현재 예상 총액 ·
 * 평가 손익 · 세트별 분류. (7·30일 가치 변화와 언어판 구성은 시세 이력 적재 후)
 *
 * 데이터는 react-query(useCollectionQuery)가 source of truth 다.
 */
import { useEffect, useState } from "react";
import { track } from "../../lib/tracking";
import { useQueryClient } from "@tanstack/react-query";
import {
  createCollectionGroup,
  renameCollectionGroup,
  type CollectionGroupItem,
} from "../../api/collectionGroup";
import {
  useCollectionGroupsQuery,
  COLLECTION_GROUP_KEY,
} from "../../hooks/useCollectionGroupQueries";
import { useMoney } from "../../hooks/useMoney";
import { useOverlayNavigate } from "../../hooks/useOverlayNavigate";
import { useTranslation } from "react-i18next";
import { Button } from "@astryxdesign/core/Button";
import { ClickableCard } from "@astryxdesign/core/ClickableCard";
import { EmptyState } from "@astryxdesign/core/EmptyState";
import { Grid } from "@astryxdesign/core/Grid";
import { DropdownMenu } from "@astryxdesign/core/DropdownMenu";
import { IconButton } from "@astryxdesign/core/IconButton";
import { Item } from "@astryxdesign/core/Item";
import { List } from "@astryxdesign/core/List";
import { SegmentedControl, SegmentedControlItem } from "@astryxdesign/core/SegmentedControl";
import { Skeleton } from "@astryxdesign/core/Skeleton";
import { Heading } from "@astryxdesign/core/Heading";
import { Text } from "@astryxdesign/core/Text";
import { Layout, LayoutContent, LayoutFooter, VStack, HStack } from "@astryxdesign/core/Layout";
import { Dialog, DialogHeader } from "@astryxdesign/core/Dialog";
import { TextInput } from "@astryxdesign/core/TextInput";
import { COLLECTION_MAX } from "../../components/collection/collectionLimits";
import { collectionGroupName } from "../../components/collection/collectionGroupName";
import {
  COLLECTION_NAME_MAX,
  canSubmitCollectionName,
  clampCollectionName,
  collectionNameLength,
} from "../../components/collection/collectionName";
import CardThumb from "../../components/card/CardThumb";
import TrendChip from "../../components/card/TrendChip";
import Icon from "../../components/primitives/Icon";
import SwipeTabPanel from "../../components/primitives/SwipeTabPanel";
import ConfirmDialog from "../../components/feedback/ConfirmDialog";
import { useToast } from "../../components/feedback/Toast";
import MisreadReportSheet from "../../components/report/MisreadReportSheet";
import { useMisreadReport } from "../../components/report/useMisreadReport";
import { cardImageUrl } from "../../api/card";
import { deleteHolding, type HoldingItem } from "../../api/collection";
import { COLLECTION_KEY, useCollectionQuery } from "../../hooks/useCollectionQueries";
import coinFront from "../../assets/pictograms/peeka-coin-front.png";
import { useAuthStore } from "../../store/authStore";
import { useRequireAuth } from "../../navigation/useRequireAuth";
import { isNative } from "../../native";
import styles from "./CollectionsPage.module.css";

/** 이 보유 건의 현재 평가액. 시세가 없으면 null — 0원으로 치지 않는다(§5.4). */
function currentTotal(h: HoldingItem): number | null {
  const amount = h.currentPrice.amount;
  if (h.currentPrice.display === "NONE" || amount === null) return null;
  return amount * h.quantity;
}

/**
 * 이 보유 건의 **30일 등락률**. 손익이 있던 자리다 (design 2026-08-25 · FN-022 폐기).
 *
 * 손익은 구매가를 알아야 나오는 값인데 적어 두는 사람이 드물어 대부분 비어 있었고,
 * 등급·표면이 어긋나면 값 자체가 틀렸다. 등락은 구매가 없이도 말할 수 있고
 * 카드 상세·시세 화면과 **같은 축**이라 두 화면이 같은 것을 말하게 된다.
 */
function rowTrend(h: HoldingItem): number | null {
  return h.currentPrice.trendRate;
}

/** 보유 카드 정렬 — 기본 평가금액 순. '수익률 순'이 '등락률 순'으로 바뀌었다 */
export type HoldingSort = "value" | "trend" | "set" | "qty" | "country";
const HOLDING_SORTS: HoldingSort[] = ["value", "trend", "set", "qty", "country"];
/** 국가 순 — 한국판 → 영문판 → 일본판 */
const LANG_ORDER: Record<string, number> = { ko: 0, en: 1, ja: 2 };

/**
 * 보유 카드 정렬. 값이 **없는 행은 지어내지 않고 뒤로 보낸다** — 시세가 아직 없는
 * 카드도, 등락을 못 내는 카드도 정상적으로 존재한다.
 */
export function sortHoldings(list: HoldingItem[], sort: HoldingSort): HoldingItem[] {
  const nullsLast = (a: number | null, b: number | null) => {
    if (a === null && b === null) return 0;
    if (a === null) return 1;
    if (b === null) return -1;
    return b - a;
  };
  const sorted = [...list];
  switch (sort) {
    case "trend":
      return sorted.sort((a, b) => nullsLast(rowTrend(a), rowTrend(b)));
    case "set":
      /* 세트로 묶고, 묶음 안에서는 **번호 순**으로 (design 2026-08-27).
         세트만 맞추고 안이 뒤죽박죽이면 도감을 펼친 게 아니라 그냥 섞인 줄이 된다 —
         같은 세트를 모으는 사람은 번호로 빈자리를 센다.
         ⚠ 번호는 '223/172' 같은 글자다. 숫자로 읽어 비교한다 — 글자로 비교하면
           10 이 2 보다 앞에 온다. 번호 없는 인쇄판(일본판 기본 에너지)은 0 으로 떨어져
           그 세트의 맨 앞에 모인다. */
      return sorted.sort(
        (a, b) =>
          a.card.cardsetName.localeCompare(b.card.cardsetName, "ko") ||
          (parseInt(a.card.number ?? "", 10) || 0) - (parseInt(b.card.number ?? "", 10) || 0) ||
          a.card.name.localeCompare(b.card.name, "ko"),
      );
    case "qty":
      return sorted.sort((a, b) => b.quantity - a.quantity);
    case "country":
      return sorted.sort(
        (a, b) => (LANG_ORDER[a.card.language] ?? 9) - (LANG_ORDER[b.card.language] ?? 9),
      );
    default:
      return sorted.sort((a, b) => nullsLast(currentTotal(a), currentTotal(b)));
  }
}

export type CollectionsTab = "holdings" | "collections";
const COLLECTIONS_TABS: readonly CollectionsTab[] = ["holdings", "collections"];
/** 탭 기억은 **세션 한정**이다 — 앱을 새로 켜면 문서 기본값(보유 카드)으로 돌아간다. */
export const TAB_KEY = "peeka.collections.tab";
export function loadTab(): CollectionsTab {
  try {
    return sessionStorage.getItem(TAB_KEY) === "collections" ? "collections" : "holdings";
  } catch {
    return "holdings";
  }
}

export default function CollectionsPage() {
  const { t } = useTranslation("card");
  const money = useMoney();
  // 보유 수정·세트 상세는 activity 로 뜬다 (뒤로가기가 컬렉션으로 돌아와야 한다).
  const overlayNavigate = useOverlayNavigate();
  const isAuthenticated = useAuthStore((s) => !!s.accessToken);
  const { requireAuth } = useRequireAuth();
  /**
   * 보유 카드 보기 — 세트 상세와 같은 두 모드지만 **기본이 다르다**.
   * 세트 상세는 앨범(그림으로 훑는 자리), 여기는 리스트다 — 보유 카드는 그림보다
   * 등급·수량·평가액을 확인하러 오는 자리다.
   */
  const [holdingView, setHoldingView] = useState<"album" | "list">("list");
  /* 정렬은 드롭다운이다 (design 2026-08-27) — 칩 다섯은 375px 에서 한 줄에 안 들어가
     목록 위에 가로 스크롤 줄이 하나 더 생겼다. 세트 상세와 같은 부품이다. */
  const [sort, setSort] = useState<HoldingSort>("value");
  /**
   * 편집 모드 — 켜면 목록형으로 고정되고 행마다 '신고 · 빼기' 가 열린다 (SC-002).
   * ⚠ 앨범 타일에는 그 두 개를 놓을 자리가 없고, 무엇을 빼는지는 이름·등급·수량이
   *   함께 보이는 줄에서 확인하는 게 맞다. 끄면 보던 뷰로 돌아간다.
   * ⚠ 켜고 끄는 손잡이는 **총액 칸**에 있다. 정렬 줄에 두면 켜는 순간 그 줄이
   *   통째로 사라지면서 끄는 버튼까지 같이 사라진다.
   */
  const [editing, setEditing] = useState(false);
  /** 빼기 확인 중인 보유 건. null 이면 확인 창이 닫혀 있다 */
  const [removing, setRemoving] = useState<HoldingItem | null>(null);
  // 묶음 (IA SC-013·SC-015) — 만들기/이름변경은 같은 다이얼로그가 맡는다(입력이 이름 하나뿐이다).
  const groups = useCollectionGroupsQuery();
  /**
   * 보유 카드 ↔ 내 컬렉션 (SC-002 rev27 · design 2026-08-14). 둘 다 "내가 가진 것"을
   * 보는 방식이라(낱장으로 보나, 묶음으로 보나) 세로로 이어 붙이면 스크롤만 길어지고
   * 둘을 견주기도 어렵다. 같은 자리에서 갈아 끼우면 한 화면에서 관점만 바뀐다.
   *
   * 탭은 **세션 안에서 기억한다** (design 2026-08-28). 내 컬렉션에서 상세로 들어갔다
   * 뒤로 오면 화면이 다시 마운트되며 기본값으로 돌아가, 방금 있던 목록이 사라져
   * "어디로 갔지" 가 됐다. 첫 진입 기본은 문서대로 보유 카드고, 앱을 새로 켜면
   * sessionStorage 가 비어 다시 보유 카드다.
   */
  const [tab, setTabState] = useState<CollectionsTab>(loadTab);
  const setTab = (next: CollectionsTab) => {
    setTabState(next);
    try { sessionStorage.setItem(TAB_KEY, next); } catch { /* storage 불가 환경 무시 */ }
  };
  // 탭이 곧 보는 내용이라 탭 전환도 조회로 센다 (마운트 1회 + 전환마다).
  useEffect(() => {
    track("collection_view", { tab });
  }, [tab]);

  const [groupEditing, setGroupEditing] = useState<
    { mode: "create" } | { mode: "rename"; group: CollectionGroupItem } | null
  >(null);
  const [groupDraft, setGroupDraft] = useState("");
  const [groupBusy, setGroupBusy] = useState(false);

  /* SC-027 — 비회원이면 시트 대신 로그인으로 간다 (판단은 훅이 한다) */
  const { reportingUid, openReport, closeReport } = useMisreadReport();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { data, isLoading, isError, refetch } = useCollectionQuery();

  const submitGroup = async () => {
    const name = groupDraft.trim();
    if (!name || !groupEditing || groupBusy) return;
    setGroupBusy(true);
    try {
      if (groupEditing.mode === "create") await createCollectionGroup(name);
      else await renameCollectionGroup(groupEditing.group.groupSerialNo, name);
      await queryClient.invalidateQueries({ queryKey: COLLECTION_GROUP_KEY });
      setGroupEditing(null);
      setGroupDraft("");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t("collection.groupFailed"));
    } finally {
      setGroupBusy(false);
    }
  };

  // §6.1 비회원은 컬렉션을 저장할 수 없다 — 목록도 없다. 로그인부터 유도한다.
  if (!isAuthenticated) {
    return (
      <div className={styles.page}>
        <EmptyState
          title={t("collection.loginRequired")}
          description={t("collection.loginRequiredBody")}
          actions={
            <Button
              variant="primary"
              label={t("collection.login")}
              clickAction={() => requireAuth({ type: "none" })}
            />
          }
        />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={styles.page}>
        <VStack gap={4}>
          <Skeleton height={96} />
          <Skeleton height={72} />
          <Skeleton height={72} />
          <Skeleton height={72} />
        </VStack>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className={styles.page}>
        <EmptyState
          title={t("collection.loadFailed")}
          actions={
            <Button
              variant="secondary"
              label={t("collection.retry")}
              clickAction={() => void refetch()}
            />
          }
        />
      </div>
    );
  }

  const { holdings, summary } = data;
  const sortedHoldings = sortHoldings(holdings, sort);
  /* 지금 신고 중인 카드 — 훅은 uid 만 들고 있다 (그 일의 전부가 로그인 판단이다) */
  const reportingCard = reportingUid
    ? holdings.find((h) => h.card.cardUid === reportingUid) ?? null
    : null;

  /**
   * 보유에서 빼기.
   *
   * ⚠ 지운 뒤에 **한 줄로 알린다.** 확인 창이 닫히고 그 행이 사라지는 것이 유일한
   *   피드백이었는데, 목록이 길면 사라진 자리가 화면 밖일 수 있어 **무슨 일이 일어났는지
   *   모른 채** 창만 닫힌 것처럼 보인다. 이름을 넣어 무엇이 빠졌는지 말한다.
   * ⚠ 되돌리기(undo)는 두지 않는다. 확인 창에서 이미 한 번 물었고, 토스트에 또 다른
   *   되돌리기를 달면 '취소할 수 있으니 대충 눌러도 된다' 가 되어 확인 창의 뜻이 흐려진다.
   */
  const handleRemove = async () => {
    if (!removing) return;
    const name = removing.card.name;
    const serialNo = removing.holdingSerialNo;
    setRemoving(null);
    try {
      await deleteHolding(serialNo);
      await queryClient.invalidateQueries({ queryKey: COLLECTION_KEY });
      toast.info(t("collection.removedHolding", { name }));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t("collection.loadFailed"));
    }
  };

  /* ⚠ 보유가 0장이어도 **화면을 통째로 비우지 않는다** (design 2026-08-28 탭 도입).
     종전에는 여기서 조기 반환해 EmptyState 하나만 그렸는데, 탭이 생긴 뒤로는 그러면
     스캔한 적 없는 사람이 '내 컬렉션' 탭에 영영 못 간다 — 컬렉션은 수동 추가로도
     만들어지므로(IA SC-016) 보유와 독립이다. 빈 안내는 보유 탭 **안에서** 낸다. */
  const holdingsEmpty = holdings.length === 0;

  /* 손익(pnlValue - pnlCost)은 2026-08-25 에 화면에서 빠졌다 — FN-022 폐기.
     서버 요약은 계속 그 값을 내려보내지만 여기서는 읽지 않는다. */
  const partiallyValued = summary.valuedQuantity < summary.totalQuantity;

  return (
    <div className={styles.page}>
      <VStack gap={6}>
        {/* 탭은 **스크롤해도 붙어 있다** (design 2026-08-28). 두 탭이 서로 다른 저장소를
            보는 화면 전환기라, 목록을 한참 내린 뒤에도 '지금 어느 쪽인가' 와 '다른 쪽으로
            가는 길' 이 손닿는 데 있어야 한다. 위로 되돌아가서야 갈아탈 수 있으면 두 탭은
            사실상 두 화면이다.
            ⚠ 폭을 꽉 채운다(layout="fill") — 둘 중 하나만 켜지는 자리라 켜진 쪽이 화면
              절반을 그대로 차지해야 무엇이 지금인지가 모양으로 읽힌다.
            ⚠ 오른쪽에 아무것도 두지 않는다. 편집·새 컬렉션은 아래 줄이다 — 탭 옆에 두면
              '탭의 일부' 로 보이는데 둘 다 **고른 탭 안에서** 하는 일이다. */}
        <div className={styles.tabsSticky}>
          <SegmentedControl
            value={tab}
            onChange={(value) => setTab(value === "collections" ? "collections" : "holdings")}
            label={t("collection.viewLabel")}
            size="sm"
            layout="fill"
          >
            <SegmentedControlItem value="holdings" label={t("collection.myCards")} />
            <SegmentedControlItem value="collections" label={t("collection.mine")} />
          </SegmentedControl>
        </div>

        {/* 탭 아래 내용만 스와이프 면이다 — sticky 탭 헤더는 밖에 둔다(overflow hidden 안에서는
            sticky 가 안 붙고, 헤더까지 슬라이드하면 어색하다). 안쪽 VStack 은 종전 형제 간격 유지. */}
        <SwipeTabPanel tabs={COLLECTIONS_TABS} activeTab={tab} onTabChange={setTab}>
        <VStack gap={6}>
        {tab === "holdings" && (holdingsEmpty ? (
          <EmptyState
            title={t("collection.empty")}
            description={t("collection.emptyBody")}
            actions={
              // 스캔은 네이티브 전용 탭이라 웹에서는 안내만 남긴다.
              isNative() ? (
                <Button
                  variant="primary"
                  label={t("collection.goScan")}
                  clickAction={() => overlayNavigate("/scan")}
                />
              ) : undefined
            }
          />
        ) : (
          <>
        {/* §6.3 총액·손익·보유 수 — **한 카드**에 모은다.
            design 2026-08-20: 2열 격자를 걷어내고 **큰 숫자 하나 + 그 아래 작은 값
            두 개**로 다시 짰다. 격자는 손익과 보유를 좌우로 갈랐는데, 둘 다 위의 총액을
            **설명하는 부속**이지 나란한 두 항목이 아니다. 한 줄에 붙여 두면
            "974,000원 — 그중 77,000원이 오른 것이고, 카드는 6장" 으로 이어 읽힌다.
            라벨은 글자 대신 아이콘 칩이 든다 — 값 자체가 이미 말한다(부호·색은 손익,
            '장' 은 수량). 스크린리더에는 아이콘 ariaLabel 로 같은 이름이 전달된다.
            ⚠ 분모 안내(valuedBasis · pnlBasis · pnlUnavailable)는 design 원본에 없지만
              **그대로 둔다** — 시세/구매가를 모르는 카드가 정상적으로 존재해서 총액과
              손익의 모수가 다르다(§6.3). 안 밝히면 총액이 그냥 낮게만 보인다. */}
        {/* 겉의 카드 상자를 걷었다 (design 2026-08-28).
            ⚠ 이 자리는 **목록이 아니라 화면의 머리말**이다. 아래 보유 카드들이 저마다
              상자를 갖고 있는데 그 위에 또 상자를 두면, 총액이 '첫 번째 항목' 처럼 보인다.
              상자를 벗기면 큰 숫자가 배경 위에 그냥 서고, 그제야 머리말이 된다.
            ⚠ 좌우 여백은 페이지 거터가 준다 — 여기서 따로 주면 아래 정렬 줄과 시작선이
              어긋난다. */}
        <div className={styles.assetZone}>
          <HStack gap={3} align="center" justify="center">
            {/* **금액이 위, 장수가 아래다.** '총 자산가치' 라벨은 걷었다 — 큰 숫자 하나에
                라벨까지 얹으면 세 줄이 되는데, 이 자리가 말하는 것은 애초에 그 값 하나다.
                여기 온 사람이 알고 싶은 것은 얼마인지고, 몇 장이 그 값을 만들었는지가
                따라온다. */}
            <VStack gap={1.5} align="center">
              {/* 코인이 금액 **바로 왼쪽**에 붙는다 — '이 돈' 을 가리키는 말머리다.
                  ⚠ 장식이다. 값은 옆의 글자가 다 말하므로 alt 를 비운다. */}
              <HStack gap={1} align="center">
                <img
                  className={styles.assetCoin}
                  src={coinFront}
                  alt=""
                  aria-hidden="true"
                  draggable={false}
                />
                <Heading level={1}>{money.format(summary.totalValue)}</Heading>
              </HStack>

              <HStack gap={0} align="center">
                {/* '스캔 N장' 이다 (design 2026-08-28). 그냥 'N장' 은 카드마다 붙는
                    장수와 같은 말이라, 이 숫자가 **어디서 온 것인지**가 없었다.
                    보유는 스캔으로만 만들어지므로(FN-028) 그 출처를 적는다. */}
                <Text type="supporting" color="secondary">
                  {t("collection.scannedCount", { count: summary.totalQuantity })}
                </Text>
                {/* 편집 — **장수 옆**이다. 편집이 바로 이 숫자를 바꾸는 일이라서고,
                    정렬 줄에 두면 켜는 순간 끄는 버튼까지 사라지기 때문이다.
                    ⚠ 바탕(data-row-action)은 주지 않는다 — 혼자 서 있어 그 네모가 튄다. */}
                <IconButton
                  label={editing ? t("collection.editDone") : t("collection.edit")}
                  variant="ghost"
                  size="sm"
                  className={styles.assetEdit}
                  icon={<Icon name={editing ? "Check" : "Edit"} size="xs" />}
                  clickAction={() => setEditing((v) => !v)}
                />
              </HStack>

              {/* 시세가 없는 카드는 총액에서 빠졌음을 밝힌다.
                  ⚠ design 원본에 없는 줄이지만 **그대로 둔다** — 시세를 모르는 카드가
                    정상적으로 존재해 총액의 모수가 보유 전체가 아니다(§6.3). 안 밝히면
                    총액이 그냥 낮게만 보인다. */}
              {partiallyValued && (
                <Text type="supporting" color="secondary">
                  {t("collection.valuedBasis", {
                    valued: summary.valuedQuantity,
                    total: summary.totalQuantity,
                  })}
                </Text>
              )}
            </VStack>
          </HStack>
        </div>

        {/* 보유 카드 목록 — 누르면 §6.2 저장 항목 수정.
            앨범형 ↔ 리스트형은 **세트 상세와 같은 부품·같은 아이콘**이다 (design
            2026-08-18) — 두 화면에서 같은 일을 다르게 생기게 두면 매번 다시 배워야
            한다. 다만 기본이 다르다: 세트 상세는 앨범(그림 훑기), 여기는 **리스트**다.
            보유 카드는 그림보다 등급·수량·평가액을 확인하러 오는 자리다. */}
        <VStack gap={3}>
          {/* 제목('보유 카드')은 탭이 이미 말한다 — 바로 아래 한 번 더 쓰면 같은 말이
              두 줄이 된다 (design 2026-08-28 탭 도입). */}

          {/* 정렬 다섯 — 평가금액 · 등락률 · 카드세트 · 보유수량 · 국가 (IA rev28).
              '수익률 순'은 2026-08-25 에 '등락률 순'이 됐다: 구매가가 사라져 수익률을
              낼 근거가 없다 (FN-022 폐기).
              ⚠ 뷰 전환이 **같은 줄**이다 (design 2026-08-28). 둘 다 '이 목록을 어떻게 볼
                것인가' 라 같은 층이고, 세트 상세(SC-006)도 같은 배치다.
              ⚠ 편집 중에는 이 줄을 통째로 감춘다. 편집은 목록을 목록형으로 잠그므로 뷰
                전환이 할 일이 없고, 정렬은 뺄 카드를 찾는 동안 건드릴 일이 아니다 —
                남겨 두면 눌러도 아무 일이 없거나(뷰) 지우려던 줄이 발밑에서 움직인다(정렬). */}
          {!editing && (
            <HStack gap={2} justify="between" align="center" width="100%">
              <HStack gap={2} align="center">
                <DropdownMenu
                  button={{
                    label: t(`collection.sort.${sort}`),
                    variant: "secondary",
                    /* Astryx 의 크기 단계는 sm·md·lg 셋뿐이고 이게 가장 작은 단이다.
                       32px 로 눌러 세트 상세의 정렬과 같은 크기로 맞춘다
                       (global.css data-compact) */
                    size: "sm",
                    "data-compact": "",
                  }}
                  items={HOLDING_SORTS.map((key) => ({
                    label: t(`collection.sort.${key}`),
                    /* 지금 고른 것에 체크 — 드롭다운은 칩과 달리 눌린 상태를 보일 자리가 없다 */
                    icon: sort === key ? <Icon name="Check" size="sm" /> : undefined,
                    onClick: () => setSort(key),
                  }))}
                />
                {/* 등락의 기준을 밝힌다 — 무엇에 견준 값인지 말하지 않으면 '샀을 때보다'로 읽힌다.
                    ⚠ 좁은 화면에서는 이 줄이 먼저 줄어든다(maxLines 1). 드롭다운은 누르는
                      것이라 못 줄이고, 이건 읽는 글이라 줄일 수 있다. */}
                {sort === "trend" && (
                  <Text type="supporting" color="secondary" maxLines={1}>
                    {t("collection.sort.trendBasis")}
                  </Text>
                )}
              </HStack>

              <SegmentedControl
                value={holdingView}
                onChange={(value) => setHoldingView(value === "album" ? "album" : "list")}
                label={t("set.viewLabel")}
                size="sm"
              >
                <SegmentedControlItem
                  value="album"
                  label={t("set.viewAlbum")}
                  isLabelHidden
                  icon={<Icon name="Card" size="sm" />}
                />
                <SegmentedControlItem
                  value="list"
                  label={t("set.viewList")}
                  isLabelHidden
                  icon={<Icon name="List" size="sm" />}
                />
              </SegmentedControl>
            </HStack>
          )}

          {/* ⚠ 행을 누르면 **카드 상세**다 (화면정의서 SC-002 화면전이 `[보유] 카드 → SC-007`).
              종전엔 보유 수정 폼으로 보냈는데, 그 폼이 묻던 구매가·구매처·보관장소·메모는
              2026-08-25 에 전부 폐기된 필드다(FN-022 삭제 · FN-037 "구매가 입력 기능 없음").
              목록에서 카드를 누르는 사람이 보려는 것은 그 카드의 시세다. */}
          {holdingView === "album" && !editing ? (
            /* 앨범형 — 모바일 2열. **홈 §1 타일과 같은 구성**이다: 아트워크 · 카드명 ·
               평가액 · 손익. 같은 '내 보유 카드'를 두 화면에서 다르게 그리면 같은
               것으로 안 읽힌다.
               ⚠ minWidth 는 본문 폭에서 역산한다 — 세트 상세 ALBUM_MIN_WIDTH 와 같은
                 계산이다(375 기준 본문 327, gap 12 → 150).
               ⚠ 목록에 있던 등급·수량·카드번호는 이 뷰에서 빠진다. 셀이 158px 이라
                 넣으면 전부 잘린다. 자세한 값은 리스트형이 맡는다. */
            <Grid gap={3} columns={{ minWidth: 150, repeat: "fill" }}>
              {sortedHoldings.map((h) => {
                const value = currentTotal(h);
                const trend = rowTrend(h);
                return (
                  <ClickableCard
                    key={h.holdingSerialNo}
                    label={h.card.name}
                    variant="transparent"
                    padding={0}
                    onClick={() => overlayNavigate(`/cards/${h.card.cardSerialNo}`)}
                  >
                    <VStack gap={1}>
                      <CardThumb
                        imgUrl={cardImageUrl(h.card.imagePathway) ?? undefined}
                        size="fill"
                      />
                      {/* 카드명은 body(16px/400) — 홈·세트 상세 앨범형과 같은 스펙이다 */}
                      <Text type="body" maxLines={1} wordBreak="break-all">{h.card.name}</Text>
                      <Text type="label" weight="bold">
                        {value === null ? t("collection.noPrice") : money.format(value)}
                      </Text>
                      {/* 손익 자리에 30일 등락 (design 2026-08-25). 등락이 없는 카드는
                          TrendChip 이 '—' 로 떨어진다 — 지어내지 않는다. */}
                      <TrendChip value={trend} />
                    </VStack>
                  </ClickableCard>
                );
              })}
            </Grid>
          ) : (
            /* 리스트형 — 밀집 데이터라 행으로 둔다 (기본) */
            <List hasDividers>
              {sortedHoldings.map((h) => {
                const value = currentTotal(h);
                const trend = rowTrend(h);
                const gradeText = h.grader === "RAW" ? t("detail.ungraded") : `${h.grader} ${h.grade}`;
                return (
                  <Item
                    key={h.holdingSerialNo}
                    as="li"
                    label={h.card.name}
                    description={t("collection.rowMeta", {
                      number: h.card.number ?? h.card.cardsetCode.toUpperCase(),
                      grade: gradeText,
                      qty: h.quantity,
                    })}
                    labelLines={1}
                    onClick={() => overlayNavigate(`/cards/${h.card.cardSerialNo}`)}
                    startContent={
                      <CardThumb imgUrl={cardImageUrl(h.card.imagePathway) ?? undefined} />
                    }
                    endContent={
                      <HStack gap={2} align="center">
                        <VStack gap={0.5} align="end">
                          <Text type="body" weight="bold">
                            {value === null ? t("collection.noPrice") : money.format(value)}
                          </Text>
                          <TrendChip value={trend} />
                        </VStack>
                        {/* 편집 중에만 열리는 두 길 — **신고**와 **빼기**.
                            ⚠ 나란히 두는 것이 요점이다. 빼기만 있으면 내 목록만 깨끗해지고
                              **모델은 그대로다** — 같은 카드를 다음에도 똑같이 틀린다.
                              두 길을 마주 세워야 '지우기 전에 알려 줄 수도 있다' 가 보인다.
                            ⚠ 이 span 은 행 전체의 클릭을 막는 껍데기이기도 하다 —
                              Astryx Item 의 endContent 는 이벤트를 안 넘겨주므로 껍데기가
                              막지 않으면 보유 수정 화면으로 샌다. */}
                        {editing && (
                          <span
                            className={styles.rowActions}
                            onClick={(e) => e.stopPropagation()}
                            role="presentation"
                          >
                            {/* ⚠ 색은 **액센트**다. 휴지통과 같은 회색이면 두 아이콘이 한
                                덩어리로 읽혀 구분되지 않는다. --color-accent(브랜드 원색)가
                                아니라 --color-icon-accent 다 — 원색은 배경으로 쓸 때의 값이라
                                아이콘으로 얹으면 대비가 모자란다 (tokens.css §accent). */}
                            <IconButton
                              label={t("report.open")}
                              variant="ghost"
                              size="sm"
                              data-row-action=""
                              icon={<Icon name="Report" size="sm" color="var(--color-icon-accent)" />}
                              clickAction={() => openReport(h.card.cardUid)}
                            />
                            <IconButton
                              label={t("collection.removeHolding")}
                              variant="ghost"
                              size="sm"
                              data-row-action=""
                              icon={<Icon name="Delete" size="sm" />}
                              clickAction={() => setRemoving(h)}
                            />
                          </span>
                        )}
                      </HStack>
                    }
                  />
                );
              })}
            </List>
          )}
        </VStack>

          </>
        ))}

        {/* 내가 만든 묶음 (IA SC-012·SC-015) — 보유 위에 얹는 분류.
            ⚠ 비어 있어도 섹션을 낸다. 만들 길이 여기밖에 없어서 숨기면 기능이 없는 것과
              같아진다(담기 시트에도 만들기가 있지만 그건 담는 순간에만 지나간다). */}
        {tab === "collections" && (
        <VStack gap={3}>
          <HStack gap={2} align="center" justify="between">
            <Heading level={3}>{t("collection.myGroups")}</Heading>
            <Button
              variant="ghost"
              size="sm"
              /* '+' 가 앞에 선다 (design 2026-08-28). 이 줄의 왼쪽은 제목이고 이 버튼만
                 동작이라, 글자만으로는 둘이 같은 무게로 읽혔다. */
              icon={<Icon name="Add" size="sm" />}
              label={t("collection.create")}
              /* 컬렉션 50개에서 죽는다 (FN-032). */
              isDisabled={(groups.data?.length ?? 0) >= COLLECTION_MAX}
              clickAction={() => { setGroupDraft(""); setGroupEditing({ mode: "create" }); }}
            />
          </HStack>
          {/* 흐리기만 하면 고장처럼 읽혀서 왜 못 누르는지를 적는다 (FN-032). */}
          {(groups.data?.length ?? 0) >= COLLECTION_MAX && (
            <Text type="supporting" color="secondary">
              {t("collection.limitReached", { max: COLLECTION_MAX })}
            </Text>
          )}
          {groups.data && groups.data.length > 0 ? (
            <Grid gap={4} columns={{ minWidth: 300, repeat: "fit" }}>
              {groups.data.map((g) => (
                /* 카드 **전체**가 상세로 가는 버튼이다 (design 2026-08-28).
                   전에는 이름 쪽만 버튼이고 오른쪽에 '이름 변경'·'삭제' 가 나란히 있었는데,
                   총액·30일 변화·썸네일이 들어오면서 좁은 폭에서 두 덩어리가 겹쳤다(실기기 확인).
                   ⚠ 두 동작을 **잃지 않는다** — 상세의 관리 메뉴(SC-015)에 둘 다 있고,
                     삭제는 거기가 정본이다. 목록에도 두면 destructive 진입점이 두 곳이 된다. */
                <ClickableCard
                  key={g.groupSerialNo}
                  label={collectionGroupName(g, t)}
                  padding={4}
                  height="fill"
                  onClick={() => overlayNavigate(`/collections/groups/${g.groupSerialNo}`)}
                >
                  <VStack gap={1} align="start" width="100%">
                    <Text weight="semibold">{collectionGroupName(g, t)}</Text>
                    <Text type="supporting" color="secondary">
                      {t("collection.cardCount", { count: g.cardCount })}
                    </Text>
                    {/* '누적' 캡션 — 이 숫자는 지금 시세가 아니라 담긴 카드 값을 **모두 더한
                        것**이다. 홈·상세의 시세 숫자와 같은 굵기라 뜻이 갈리지 않으니 앞에
                        성격을 한 단어로 단다.
                        ⚠ 오른쪽에 붙는 것은 30일 변화율 **하나**다. 금액 변화까지 붙이면
                          한 줄에 원이 둘 서서 총액이 어느 쪽인지 흐려진다.
                        ⚠ 변화율은 서버가 모르면 null 로 온다 — 그때는 아무것도 그리지 않는다.
                          0% 를 그리면 "안 변했다"가 되는데 실제로는 "모른다"다. */}
                    {g.totalValue !== null && (
                      <HStack gap={2} align="center" wrap="wrap">
                        <Text type="supporting" color="secondary">{t("collection.totalCaption")}</Text>
                        <Text weight="bold">{money.format(g.totalValue)}</Text>
                        {g.trendRate !== null && <TrendChip value={g.trendRate} />}
                      </HStack>
                    )}
                    {/* 미리보기 썸네일 — 다섯 장까지, **최근 담은 순**(서버가 잘라서 준다).
                        방금 넣은 카드가 바로 보여야 "담겼다" 가 눈으로 닫힌다.
                        ⚠ 남은 수는 '+N' 으로 말한다. 그냥 자르면 다섯 장짜리와 쉰 장짜리가
                          똑같아 보인다. */}
                    {g.previewImages.length > 0 && (
                      <HStack gap={1} align="center" wrap="wrap">
                        {g.previewImages.map((path) => (
                          <CardThumb key={path} imgUrl={cardImageUrl(path) ?? undefined} />
                        ))}
                        {g.cardCount > g.previewImages.length && (
                          <Text type="supporting" color="secondary">
                            {/* '+N' 도 문구 사전을 거친다 (design) — 화면 문자열은 한 곳에서 센다 */}
                            {t("collection.morePreview", {
                              count: g.cardCount - g.previewImages.length,
                            })}
                          </Text>
                        )}
                      </HStack>
                    )}
                  </VStack>
                </ClickableCard>
              ))}
            </Grid>
          ) : (
            <Text type="supporting" color="secondary">{t("collection.groupsEmpty")}</Text>
          )}
        </VStack>
        )}
        </VStack>
        </SwipeTabPanel>

        {/* 세트별 진행률은 여기서 **걷었다** (design 2026-08-03 · 사용자 확정 2026-09-03).
            세트 소유 진행도는 **세트 상세(SC-006 · FN-007)** 의 항목이라 그쪽에만 둔다 —
            같은 표가 두 화면에 있으면 어느 쪽이 정본인지가 흐려진다.
            ⚠ BE 는 그대로다(`findSetProgress` · 응답 setProgress). 세트 상세가 쓰고 있고,
              되돌릴 때 서버부터 다시 만들 이유가 없다. */}
      </VStack>

      {/* 묶음 만들기 / 이름 변경 (SC-013·SC-015) — 입력이 이름 하나뿐이라 한 다이얼로그다 */}
      <Dialog isOpen={groupEditing !== null} onOpenChange={(o) => { if (!o) setGroupEditing(null); }} width={360}>
        <Layout
          header={
            <DialogHeader
              title={groupEditing?.mode === "rename"
                ? t("collection.renameTitle")
                : t("collection.createTitle")}
              onOpenChange={(o) => { if (!o) setGroupEditing(null); }}
            />
          }
          content={
            <LayoutContent>
              {/* 이름 규칙(FN-033·FN-035): 20자에서 입력이 멈추고 아래에 카운터가 선다.
                  앞뒤 공백은 저장 시 trim, 중복은 허용한다. */}
              {/* ⚠ 카운터는 TextInput **밖**의 Astryx Text 다 (2026-09-03). design 원본은
                  description 자리에 넣고 global.css 로 Astryx Field 의 flex 를 grid 로
                  갈아 라벨 줄 오른쪽에 세웠는데, 그 규칙이 Field 내부 DOM
                  (.astryx-field-label + span)을 :has() 로 파고들어 Astryx 를 올리면
                  조용히 깨진다. 입력 아래 오른쪽 정렬이면 지역 CSS 없이 같은 일을 한다. */}
              <TextInput
                label={t("collection.nameLabel")}
                value={groupDraft}
                onChange={(v) => setGroupDraft(clampCollectionName(v))}
                placeholder={t("collection.namePlaceholder")}
                isDisabled={groupBusy}
              />
              <Text type="supporting" display="block" justify="end">
                {t("collection.nameCount", { n: collectionNameLength(groupDraft), max: COLLECTION_NAME_MAX })}
              </Text>
            </LayoutContent>
          }
          footer={
            <LayoutFooter>
              <HStack gap={2} justify="end">
                <Button
                  variant="ghost"
                  label={t("collection.cancel")}
                  isDisabled={groupBusy}
                  clickAction={() => setGroupEditing(null)}
                />
                <Button
                  variant="primary"
                  label={groupEditing?.mode === "rename"
                    ? t("collection.confirmRename")
                    : t("collection.confirmCreate")}
                  isDisabled={groupBusy || !canSubmitCollectionName(groupDraft)}
                  clickAction={() => void submitGroup()}
                />
              </HStack>
            </LayoutFooter>
          }
        />
      </Dialog>

      {/* 빼기 확인 — 되돌릴 수 없는 일이라 한 번 묻는다 */}
      <ConfirmDialog
        open={removing !== null}
        onClose={() => setRemoving(null)}
        title={t("collection.removeHoldingTitle")}
        description={t("collection.removeHoldingBody", { name: removing?.card.name ?? "" })}
        secondaryText={t("action.cancel", { ns: "common" })}
        primaryText={t("collection.removeHoldingConfirm")}
        onSecondaryPressed={() => setRemoving(null)}
        onPrimaryPressed={() => void handleRemove()}
      />

      {/* SC-027 신고 시트 — 지금 신고 중인 카드는 목록에서 찾는다.
          훅은 uid 만 들고 있고(로그인 판단이 그 일의 전부다), 카드 본문은 이미 화면이
          갖고 있어 서버에 다시 물을 이유가 없다. */}
      {reportingCard && (
        <MisreadReportSheet
          open
          onClose={closeReport}
          card={{
            cardUid: reportingCard.card.cardUid,
            cardSerialNo: reportingCard.card.cardSerialNo,
            name: reportingCard.card.name,
            number: reportingCard.card.number,
            cardsetCode: reportingCard.card.cardsetCode,
            imagePathway: reportingCard.card.imagePathway,
          }}
          scannedAt={reportingCard.registeredAt}
          /* 접수 완료 화면에서만 열리는 길이다. 신고가 자동으로 빼지는 않는다 —
             자동으로 지우면 신고가 삭제 버튼처럼 쓰여 학습 데이터가 오염된다. */
          onRemoveHolding={() => setRemoving(reportingCard)}
        />
      )}
    </div>
  );
}
